/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useRef, useState } from 'react';

import { css, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { useSearchTriggerInput } from '../search-trigger-context';

const inputStyles = css({
	appearance: 'none',
	border: 'none',
	outline: 'none',
	backgroundColor: 'transparent',
	height: '100%',
	width: '100%',
	font: token('font.body.large'),
	color: token('color.text'),
	boxSizing: 'border-box',
	paddingBlockStart: '0',
	paddingBlockEnd: '0',
	paddingInlineStart: token('space.600'),
	paddingInlineEnd: token('space.100'),
});

export const SearchTriggerInputElement = ({
	id,
	inputLabel,
}: {
	id: string;
	inputLabel: string;
}): JSX.Element => {
	const { value, onChange, setIsFocused } = useSearchTriggerInput();
	// store the local value in state to avoid the input being "locked in" as a Latin character
	const [localValue, setLocalValue] = useState(value);
	const isComposingRef = useRef(false);

	// Sync local value with controlled value when not composing
	useEffect(() => {
		if (!isComposingRef.current) {
			setLocalValue(value);
		}
	}, [value]);

	return (
		<input
			data-testid="csm-ai-search-input"
			id={id}
			css={inputStyles}
			value={localValue}
			name="message"
			onChange={(e) => {
				const newValue = e.currentTarget.value;
				setLocalValue(newValue);
				// Only propagate to parent when not composing (IME input)
				// This fixes Japanese/Chinese/Korean input where the first character
				// would otherwise be "locked in" as a Latin character
				if (!isComposingRef.current) {
					onChange?.(newValue);
				}
			}}
			onCompositionStart={() => {
				isComposingRef.current = true;
			}}
			onCompositionEnd={(e) => {
				isComposingRef.current = false;
				// Commit the final composed value after IME conversion completes
				onChange?.(e.currentTarget.value);
			}}
			onFocus={() => setIsFocused(true)}
			onBlur={() => setIsFocused(false)}
			autoComplete="off"
			spellCheck="false"
			aria-label={inputLabel}
		/>
	);
};
